import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, patchStrokes, patchRound, claimGroup, releaseGroup } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ArrowLeft, Minus, Plus, CaretLeft, ChartBar, CloudSlash, UsersThree, Lock, LockOpen, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SyncStatusPill from '../components/SyncStatusPill';
import OwnerPasswordDialog from '../components/OwnerPasswordDialog';
import InstallPrompt from '../components/InstallPrompt';
import { setScoreEditUnlockToken, getScoreEditUnlockToken, getGroupClaimData, setGroupClaimData, clearGroupClaim } from '../lib/ownerAuth';
import { getTripAccess } from '../lib/tripAccess';
import { setEnteringScores } from '../lib/updateGuard';
import {
  stablefordPoints, shotsOnHole,
  teeTimeForGroup, startHoleForGroup,
  playingOrderForGroup,
  isMultiStartRound, formatPlayingHandicap, getEffectivePH, displayWHS,
} from '../golf';
import { effectiveCourseForPlayer } from '../lib/effectiveCourse';
import { roundFormat, roundHasScores } from '../lib/formatHelpers';
import { sortedGroupIndexes, groupDisplayName, holeLabelForGroup } from '../lib/groupOrder';
import { computeTeamPH, computeTeamNDB, driveCountsForTeam, driveWarnings, scrambleMissingHoles } from '../lib/scramble';
import { writeScoringHole, resumeHole } from '../lib/scoringResume';

function scoreColorClass(strokes, par) {
  if (!strokes || strokes <= 0) return '';
  const diff = strokes - par;
  if (diff <= -2) return 'score-eagle';
  if (diff === -1) return 'score-birdie';
  if (diff === 0) return 'score-par';
  if (diff === 1) return 'score-bogey';
  if (diff === 2) return 'score-double-bogey';
  return 'score-triple-plus';
}

function getGroupKey(code, roundId) {
  return `foretour:scoringGroup:${code}:${roundId}`;
}

export default function ScrambleScoring() {
  const { code, roundId } = useParams();
  const navigate = useNavigate();

  // Update guard: prevent auto-reload while entering scores
  useEffect(() => { setEnteringScores(true); return () => setEnteringScores(false); }, []);

  const tripCacheKey = `foretour:trip:${code}`;
  const [trip, setTrip] = useState(() => {
    try { const c = sessionStorage.getItem(tripCacheKey); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [round, setRound] = useState(() => trip?.rounds?.find(r => r.id === roundId) || null);
  const [loading, setLoading] = useState(!trip || !round);
  const [myGroupIdx, setMyGroupIdx] = useState(-1);
  const [groupSelected, setGroupSelected] = useState(false);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [currentHole, setCurrentHole] = useState(0);
  const [savingKey, setSavingKey] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [unsavedHoles, setUnsavedHoles] = useState(new Set());
  const [sessionExpired, setSessionExpired] = useState(false);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);
  const [missingHolesDialog, setMissingHolesDialog] = useState(null);
  const [driveUnderMinDialog, setDriveUnderMinDialog] = useState(null);
  const [confirmGroupIdx, setConfirmGroupIdx] = useState(null);
  const [allParConfirmOpen, setAllParConfirmOpen] = useState(false);
  const [scoreUnlockDialogOpen, setScoreUnlockDialogOpen] = useState(false);
  const [scoreEditUnlocked, setScoreEditUnlocked] = useState(false);
  const patchInFlightRef = useRef(false);

  // Score input editing state
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef(null);

  const etagRef = useRef(null);

  const load = useCallback(async () => {
    if (patchInFlightRef.current) return;
    try {
      const res = await getTrip(code, etagRef.current);
      if (res.status === 304) return;
      etagRef.current = (res.headers?.etag || '').replace('W/', '') || null;
      setTrip(res.data);
      try { sessionStorage.setItem(tripCacheKey, JSON.stringify(res.data)); } catch {}
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) setRound(r);
    } catch {} finally { setLoading(false); }
  }, [code, roundId, tripCacheKey]);

  useEffect(() => { load(); }, [load]);
  // Visibility-aware polling: pause when hidden, resume on return
  useEffect(() => {
    let iv = null;
    const start = () => { if (iv) clearInterval(iv); iv = setInterval(load, 8000); };
    const stop = () => { if (iv) { clearInterval(iv); iv = null; } };
    const onVis = () => { if (document.hidden) stop(); else { load(); start(); } };
    start();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', stop);
    window.addEventListener('pageshow', () => { if (!document.hidden) { load(); start(); } });
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('pagehide', stop); };
  }, [load]);

  // Restore saved group
  useEffect(() => {
    if (round && !groupSelected) {
      const saved = localStorage.getItem(getGroupKey(code, roundId));
      if (saved != null) {
        const idx = parseInt(saved, 10);
        if (idx >= 0 && idx < (round.teams?.length || 0)) {
          setMyGroupIdx(idx);
          setGroupSelected(true);
          const ts = round.team_hole_strokes?.[String(idx)] || [];
          const order = playingOrderForGroup(round, idx);
          setCurrentHole(resumeHole(code, roundId, String(idx), ts, order));
        }
      }
    }
  }, [round, groupSelected, code, roundId]);

  // Persist current hole to sessionStorage on every change
  useEffect(() => {
    if (groupSelected && round && myGroupIdx >= 0) {
      writeScoringHole(code, roundId, String(myGroupIdx), currentHole);
    }
  }, [currentHole, code, roundId, myGroupIdx, groupSelected, round]);


  // Check score-edit unlock token
  useEffect(() => {
    if (code && roundId) setScoreEditUnlocked(!!getScoreEditUnlockToken(code, roundId));
  }, [code, roundId]);

  const isSessionError = (e) => e?.response?.data?.code === 'invalid_trip_access' || e?.response?.data?.detail?.includes?.('trip access');
  const handleSessionExpired = () => { setSessionExpired(true); };

  const access = getTripAccess(code);
  const role = access?.role || (access?.legacy ? 'organiser' : null);
  const isOrganiser = role === 'organiser';
  const teams = round?.teams || [];
  const players = trip?.players || [];
  const fmt = roundFormat(round);
  const isRevealed = !!round?.revealed;
  const isGroupFinished = (round?.group_finished || [])[myGroupIdx] || false;
  const scoreLocked = (isGroupFinished || isRevealed) && !scoreEditUnlocked;

  // Current team data
  const tk = String(myGroupIdx);
  const teamPids = teams[myGroupIdx] || [];
  const teamPlayers = teamPids.map(pid => players.find(p => p.id === pid)).filter(Boolean);
  const teamPH = round && trip && groupSelected ? computeTeamPH(round, myGroupIdx, trip) : 0;

  const teamStrokes = round?.team_hole_strokes?.[tk] || [];
  const teamScores = round?.team_hole_scores?.[tk] || [];
  const teamNrHoles = round?.team_hole_nr?.[tk] || [];
  const teamDriveOwners = round?.team_drive_owner?.[tk] || [];

  const playingOrder = round && groupSelected ? playingOrderForGroup(round, myGroupIdx) : Array.from({length: 18}, (_, i) => i);
  const currentPosInOrder = playingOrder.indexOf(currentHole);
  const par = round?.hole_pars?.[currentHole] ?? round?.course?.hole_pars?.[currentHole] ?? 4;
  const si = round?.stroke_indexes?.[currentHole] ?? round?.course?.stroke_indexes?.[currentHole] ?? 1;
  const teamGross = teamStrokes[currentHole] || 0;
  const teamStab = teamScores[currentHole] ?? -1;
  const isHoleNr = teamNrHoles.includes(currentHole);
  const currentDriveOwner = teamDriveOwners[currentHole] || '';
  const shots = shotsOnHole(teamPH, si);

  const selectedGroupName = groupDisplayName(round, myGroupIdx);
  const driveCounts = round && groupSelected ? driveCountsForTeam(round, myGroupIdx) : {};
  const warns = round && groupSelected ? driveWarnings(round, myGroupIdx) : [];
  const minDrives = round?.scramble_config?.min_drives ?? 0;
  const teamTotal = teamScores.reduce((s, v) => s + Math.max(0, v ?? 0), 0);
  const currentTeeTime = round ? teeTimeForGroup(round, myGroupIdx) : null;
  const colorClass = scoreColorClass(teamGross, par);

  // ── Driver-selection lock rule (computed on the fly per hole — no stale state) ──
  const driverLock = (() => {
    if (minDrives <= 0 || !round || !groupSelected) return { mustTake: [], cannotMeet: [], lockActive: false, lockMessage: null };

    const owners = round.team_drive_owner?.[tk] || [];
    const strokes = round.team_hole_strokes?.[tk] || [];

    // Count scored holes excluding current hole
    let scoredExcCurrent = 0;
    for (let h = 0; h < 18; h++) {
      if (h === currentHole) continue;
      if ((strokes[h] ?? 0) > 0) scoredExcCurrent++;
    }
    const holesRemaining = 18 - scoredExcCurrent; // includes current hole

    // Drives used elsewhere (all holes except current) per player
    const dueMap = {};
    for (const pid of teamPids) dueMap[pid] = 0;
    for (let h = 0; h < 18; h++) {
      if (h === currentHole) continue;
      const pid = owners[h];
      if (pid && pid in dueMap) dueMap[pid]++;
    }

    const mustTake = [];
    const cannotMeet = [];

    for (const pid of teamPids) {
      const needed = minDrives - (dueMap[pid] || 0);
      if (needed > 0 && needed === holesRemaining) mustTake.push(pid);
      else if (needed > 0 && needed > holesRemaining) cannotMeet.push(pid);
    }

    // 1. cannot_meet non-empty → no lock, allow any player, existing red warning fires
    if (cannotMeet.length > 0) return { mustTake, cannotMeet, lockActive: false, lockMessage: null };
    // 2. must_take === 1 → lock to that player, soft message
    if (mustTake.length === 1) {
      const p = players.find(pl => pl.id === mustTake[0]);
      return { mustTake, cannotMeet, lockActive: true, lockMessage: { type: 'soft', text: `${p?.name || '?'} must take the remaining drives to meet the minimum` } };
    }
    // 3. must_take >= 2 → lock to those players, hard message
    if (mustTake.length >= 2) {
      const names = mustTake.map(pid => players.find(pl => pl.id === pid)?.name || '?').join(', ');
      return { mustTake, cannotMeet, lockActive: true, lockMessage: { type: 'hard', text: `Only ${names} can be selected — one will finish below the minimum` } };
    }
    // 4. No restriction
    return { mustTake: [], cannotMeet: [], lockActive: false, lockMessage: null };
  })();

  // ── Group selection ──
  const selectGroup = async (idx) => {
    if (teams.length > 0) {
      const claimantName = isOrganiser ? 'Organiser' : 'Scorer';
      const myClaimData = getGroupClaimData(code, roundId);
      const alreadyMine = myClaimData?.group_idx === idx && myClaimData?.token;
      if (!alreadyMine) {
        const claims = round?.group_claims || [];
        const existing = claims.find(c => c.group_idx === idx && new Date(c.expires_at) > new Date());
        if (existing && isOrganiser) {
          const label = existing.by_organiser ? 'another organiser' : existing.claimant_name;
          if (!window.confirm(`This group is claimed by ${label}. Continue as organiser?`)) return;
        }
        try {
          const res = await claimGroup(code, roundId, { group_idx: idx, claimant_name: claimantName });
          setGroupClaimData(code, roundId, { token: res.data.token, group_idx: idx, expires_at: res.data.expires_at });
        } catch (e) {
          if (e.response?.status === 409) toast.error(`Group already claimed by ${e.response?.data?.claimed_by || 'someone'}.`);
          else toast.error('Unable to claim group.');
          load();
          return;
        }
      }
    }
    setMyGroupIdx(idx);
    setGroupSelected(true);
    setShowGroupPicker(false);
    localStorage.setItem(getGroupKey(code, roundId), String(idx));
    if (round) {
      const ts = round.team_hole_strokes?.[String(idx)] || [];
      const order = playingOrderForGroup(round, idx);
      setCurrentHole(resumeHole(code, roundId, String(idx), ts, order));
    }
  };

  // ── Score entry handlers ──
  const patchTeamScore = async (hole, gross, stab, driveOwner, nr) => {
    setSavingKey(`team-${tk}-${hole}`);
    patchInFlightRef.current = true;
    try {
      const body = { playerId: teamPids[0] || '', hole, value: gross, team_index: myGroupIdx };
      if (stab != null) body.team_stableford = stab;
      if (driveOwner != null) body.team_drive_owner_pid = driveOwner;
      if (nr != null) body.team_nr = nr;
      await patchStrokes(code, roundId, body);
      setLastSynced(new Date());
      setUnsavedHoles(prev => { const n = new Set(prev); n.delete(`team-${hole}`); return n; });
    } catch (e) {
      if (e?.response?.status === 403 && e?.response?.data?.detail === 'player_not_in_claimed_group') {
        clearGroupClaim(code, roundId);
        setGroupSelected(false);
        setShowGroupPicker(true);
        toast.error('Group claim expired. Please re-select your group.');
      } else if (isSessionError(e)) {
        handleSessionExpired();
      } else {
        setUnsavedHoles(prev => new Set(prev).add(`team-${hole}`));
      }
    } finally { setSavingKey(null); patchInFlightRef.current = false; }
  };

  const handleTeamStroke = async (delta) => {
    if (scoreLocked || isHoleNr) return;
    // First press on an empty hole → set to par (matching individual scoring behaviour)
    const newVal = teamGross === 0 ? par : Math.max(1, teamGross + delta);
    const stab = stablefordPoints(newVal, par, teamPH, si);
    setRound(prev => {
      if (!prev) return prev;
      const ths = { ...prev.team_hole_strokes }; const thsc = { ...prev.team_hole_scores };
      ths[tk] = [...(ths[tk] || Array(18).fill(0))];
      thsc[tk] = [...(thsc[tk] || Array(18).fill(-1))];
      ths[tk][currentHole] = newVal;
      thsc[tk][currentHole] = Math.max(0, stab ?? 0);
      return { ...prev, team_hole_strokes: ths, team_hole_scores: thsc };
    });
    patchTeamScore(currentHole, newVal, Math.max(0, stab ?? 0), currentDriveOwner || null, null);
  };

  const handleDirectInput = async (absoluteValue) => {
    if (scoreLocked || isHoleNr) return;
    const stab = stablefordPoints(absoluteValue, par, teamPH, si);
    setRound(prev => {
      if (!prev) return prev;
      const ths = { ...prev.team_hole_strokes }; const thsc = { ...prev.team_hole_scores };
      ths[tk] = [...(ths[tk] || Array(18).fill(0))];
      thsc[tk] = [...(thsc[tk] || Array(18).fill(-1))];
      ths[tk][currentHole] = absoluteValue;
      thsc[tk][currentHole] = Math.max(0, stab ?? 0);
      return { ...prev, team_hole_strokes: ths, team_hole_scores: thsc };
    });
    patchTeamScore(currentHole, absoluteValue, Math.max(0, stab ?? 0), currentDriveOwner || null, null);
  };

  const handleSetDriveOwner = async (pid) => {
    if (scoreLocked) return;
    setRound(prev => {
      if (!prev) return prev;
      const tdo = { ...prev.team_drive_owner };
      tdo[tk] = [...(tdo[tk] || Array(18).fill(''))];
      tdo[tk][currentHole] = pid;
      return { ...prev, team_drive_owner: tdo };
    });
    patchTeamScore(currentHole, teamGross > 0 ? teamGross : 0, null, pid, null);
  };

  const handleToggleNr = async () => {
    if (scoreLocked || fmt === 'nsp') return;
    const wasNr = isHoleNr;
    if (wasNr) {
      setRound(prev => {
        if (!prev) return prev;
        const ths = { ...prev.team_hole_strokes }; const thsc = { ...prev.team_hole_scores }; const tnr = { ...prev.team_hole_nr };
        ths[tk] = [...(ths[tk] || Array(18).fill(0))]; thsc[tk] = [...(thsc[tk] || Array(18).fill(-1))];
        ths[tk][currentHole] = 0; thsc[tk][currentHole] = -1;
        tnr[tk] = (tnr[tk] || []).filter(h => h !== currentHole);
        return { ...prev, team_hole_strokes: ths, team_hole_scores: thsc, team_hole_nr: tnr };
      });
      patchTeamScore(currentHole, 0, -1, null, false);
    } else {
      const ndb = computeTeamNDB(round, myGroupIdx, trip, currentHole);
      setRound(prev => {
        if (!prev) return prev;
        const ths = { ...prev.team_hole_strokes }; const thsc = { ...prev.team_hole_scores }; const tnr = { ...prev.team_hole_nr };
        ths[tk] = [...(ths[tk] || Array(18).fill(0))]; thsc[tk] = [...(thsc[tk] || Array(18).fill(-1))];
        ths[tk][currentHole] = ndb; thsc[tk][currentHole] = 0;
        tnr[tk] = [...(tnr[tk] || []), currentHole];
        return { ...prev, team_hole_strokes: ths, team_hole_scores: thsc, team_hole_nr: tnr };
      });
      patchTeamScore(currentHole, ndb, 0, null, true);
    }
  };

  // ── Navigation ──
  const handleNext = async () => {
    // Guard: driver required before advancing
    const driveOwnerSet = (round?.team_drive_owner?.[tk] || [])[currentHole];
    if (!driveOwnerSet) {
      toast.error('Select whose drive was used');
      return;
    }
    // Check if team hasn't scored this hole yet (gross === 0 and not NR)
    const holeGross = (round?.team_hole_strokes?.[tk] || [])[currentHole] || 0;
    const holeIsNr = (round?.team_hole_nr?.[tk] || []).includes(currentHole);
    if (holeGross === 0 && !holeIsNr) {
      setAllParConfirmOpen(true);
      return;
    }
    advanceFromHole();
  };

  const handleAllParConfirm = async () => {
    setAllParConfirmOpen(false);
    // Auto-fill par for this hole
    await handleDirectInput(par);
    advanceFromHole();
  };

  const advanceFromHole = () => {
    if (currentPosInOrder < 17) { setCurrentHole(playingOrder[currentPosInOrder + 1]); return; }
    if (isGroupFinished) { navigate(`/trip/${code}/round/${roundId}/setup`); return; }
    // Navigate to Review Scorecard — handles missing holes, drive warnings, and lock
    navigate(`/trip/${code}/round/${roundId}/review/${myGroupIdx}`);
  };
  const handlePrev = () => { if (currentPosInOrder > 0) setCurrentHole(playingOrder[currentPosInOrder - 1]); };

  const doFinishRound = async () => {
    try {
      const gf = [...(round.group_finished || Array(teams.length).fill(false))];
      while (gf.length <= myGroupIdx) gf.push(false);
      gf[myGroupIdx] = true;
      await patchRound(code, roundId, { group_finished: gf });
    } catch {}
    navigate(`/trip/${code}/round/${roundId}/setup`);
  };

  const handleMissingMarkNr = async () => {
    const missing = missingHolesDialog;
    setMissingHolesDialog(null);
    for (const h of missing) {
      const ndb = computeTeamNDB(round, myGroupIdx, trip, h);
      try { await patchStrokes(code, roundId, { playerId: teamPids[0] || '', hole: h, value: ndb, team_index: myGroupIdx, team_stableford: 0, team_nr: true }); } catch {}
    }
    await load();
    const dw = driveWarnings(round, myGroupIdx);
    if (dw.length > 0) setDriveUnderMinDialog(dw);
    else setFinishConfirmOpen(true);
  };

  const startEdit = () => { setInputVal(teamGross > 0 ? String(teamGross) : ''); setEditing(true); setTimeout(() => inputRef.current?.focus(), 50); };
  const commitEdit = () => {
    setEditing(false);
    const v = parseInt(inputVal, 10);
    if (isNaN(v) || v < 0 || v > 15) return;
    if (v === teamGross) return;
    handleDirectInput(v);
  };

  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;
  if (!round) return null;

  const pickerOpen = !groupSelected || showGroupPicker;

  // ── Team scoring screen (mirrors individual Scoring.jsx layout) ──
  return (
    <>
    {/* ── Group picker popup (mirrors individual's "Which group are you scoring?") ── */}
    <Dialog open={pickerOpen} onOpenChange={(open) => {
      if (!open) {
        if (groupSelected) { setShowGroupPicker(false); }
        else { navigate(`/trip/${code}/round/${roundId}/setup`); }
      }
    }}>
      <DialogContent className="sm:max-w-md mx-auto" data-testid="group-picker-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#1D242B]">Which group are you scoring?</DialogTitle>
          <DialogDescription className="text-sm text-[#455561]">Select your team to filter the scoring view.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
          {(round ? sortedGroupIndexes(round) : teams.map((_, i) => i)).map(idx => {
            const teamPlayerIds = teams[idx] || [];
            const groupName = groupDisplayName(round, idx);
            const teeTime = teeTimeForGroup(round, idx);
            const sh = startHoleForGroup(round, idx);
            const names = teamPlayerIds.map(id => {
              const pl = players.find(p => p.id === id);
              if (!pl) return null;
              const plPh = getEffectivePH(pl, effectiveCourseForPlayer(round, pl.id));
              return `${pl.name} (PH: ${formatPlayingHandicap(plPh)})`;
            }).filter(Boolean).join(', ');
            const claim = (round.group_claims || []).find(c => c.group_idx === idx);
            const claimActive = claim && new Date(claim.expires_at) > new Date();
            const myClaimData = getGroupClaimData(code, roundId);
            const isMyGroup = myClaimData?.group_idx === idx && myClaimData?.token;
            const claimedByOther = claimActive && !isMyGroup;
            const isFinished = Boolean(round?.group_finished?.[idx]);
            const disabledForUser = (claimedByOther || isFinished) && !isOrganiser;

            const hasScoresInTeam = claimActive && isOrganiser && (() => {
              const ths = round?.team_hole_strokes?.[String(idx)] || [];
              return ths.some(s => s && s > 0);
            })();

            const handleRelease = async (e) => {
              e.stopPropagation();
              try {
                const res = await releaseGroup(code, roundId, { group_idx: idx });
                const updatedRound = res.data.rounds.find(r => r.id === roundId);
                if (updatedRound) setRound(updatedRound);
                if (isMyGroup) clearGroupClaim(code, roundId);
                toast.success(`${groupName} released`);
              } catch (err) {
                if (err?.response?.data?.detail === 'scores_exist') toast.error("Can't release — scores already entered");
                else toast.error('Failed to release group');
              }
            };

            const tph = computeTeamPH(round, idx, trip);

            return (
              <button key={idx} onClick={() => !disabledForUser && setConfirmGroupIdx(idx)}
                disabled={disabledForUser}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  isMyGroup ? 'border-[#1B4332] bg-[#E8E5DB]'
                  : (claimedByOther || isFinished) ? `border-[#E1E6E4] bg-[#F9F9F9] opacity-50${disabledForUser ? ' cursor-not-allowed' : ''}`
                  : myGroupIdx === idx && groupSelected ? 'border-[#1B4332] bg-[#E8E5DB]'
                  : 'border-[#E1E6E4] bg-white hover:border-[#1B4332]/50'
                }`} data-testid={`pick-group-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-bold ${(claimedByOther || isFinished) ? 'text-[#B8C7C0]' : 'text-[#1D242B]'}`}>{groupName}</span>
                  <div className="flex items-center gap-2">
                    {isMyGroup && <Badge className="bg-[#1B4332] text-white text-[10px]">Your group</Badge>}
                    {isFinished && <Badge className="bg-[#455561] text-white text-[10px]">Finished</Badge>}
                    {claimedByOther && !isFinished && <Badge className="bg-[#E59000] text-white text-[10px]">{claim.by_organiser ? 'Claimed by organiser' : `Claimed by ${claim.claimant_name}`}</Badge>}
                    <Badge className="bg-[#1B4332] text-white text-[10px]">Team PH {formatPlayingHandicap(tph)}</Badge>
                    <span className="text-xs text-[#455561]">{teamPlayerIds.length} players</span>
                    {teeTime && <Badge className="bg-[#1D242B] text-white text-[10px] font-mono px-1.5">{teeTime}</Badge>}
                    {!(round?.start_type === 'standard' && sh === (round?.starting_hole || 1)) && (
                      <Badge className="bg-[#E59000] text-white text-[10px] px-1.5">Hole {holeLabelForGroup(round, idx).slice(1)}</Badge>
                    )}
                  </div>
                </div>
                {names && <p className="text-xs text-[#455561] mt-1">{names}</p>}
                {isOrganiser && claimActive && (
                  <div className="mt-2 flex items-center justify-end">
                    {hasScoresInTeam ? (
                      <span className="text-[10px] text-[#B8C7C0] italic" data-testid={`release-blocked-${idx}`}>Can't release — scores entered</span>
                    ) : (
                      <span onClick={handleRelease}
                        className="text-[10px] font-semibold text-[#D32F2F] hover:text-[#B71C1C] cursor-pointer px-2 py-1 rounded-md hover:bg-[#FFF3F3] transition-colors"
                        data-testid={`release-group-${idx}`}>Release</span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <button onClick={() => {
              if (!groupSelected && teams.length > 0) {
                setMyGroupIdx(0);
                setGroupSelected(true);
                localStorage.setItem(getGroupKey(code, roundId), '0');
                if (round) setCurrentHole(startHoleForGroup(round, 0) - 1);
              }
              setShowGroupPicker(false);
            }}
            className="w-full text-center text-sm text-[#455561] py-2 mt-1 hover:text-[#1D242B]"
            data-testid="skip-group-picker">Skip — show all groups</button>
      </DialogContent>
    </Dialog>

    {/* ── Group confirmation popup (mirrors individual's confirm modal) ── */}
    <Dialog open={confirmGroupIdx !== null} onOpenChange={(open) => { if (!open) setConfirmGroupIdx(null); }}>
      <DialogContent className="sm:max-w-sm mx-auto" data-testid="group-confirm-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#1D242B]">Confirm your group</DialogTitle>
          <DialogDescription className="text-sm text-[#455561]">
            Make sure these are the players in your group.
          </DialogDescription>
        </DialogHeader>
        {confirmGroupIdx !== null && (() => {
          const gIdx = confirmGroupIdx;
          const gName = groupDisplayName(round, gIdx);
          const gTeeTime = round ? teeTimeForGroup(round, gIdx) : null;
          const gStartHole = round ? startHoleForGroup(round, gIdx) : 1;
          const gPlayerIds = teams[gIdx] || [];
          const gPlayers = gPlayerIds.map(id => players.find(p => p.id === id)).filter(Boolean);
          const gTph = computeTeamPH(round, gIdx, trip);
          return (
            <div className="mt-1">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-base font-bold text-[#1D242B]">{gName}</span>
                <Badge className="bg-[#1B4332] text-white text-[10px]">Team PH {formatPlayingHandicap(gTph)}</Badge>
                {gTeeTime && <Badge className="bg-[#1D242B] text-white text-[10px] font-mono px-1.5">{gTeeTime}</Badge>}
                {gStartHole > 1 && <Badge className="bg-[#E59000] text-white text-[10px] px-1.5">Hole {gStartHole}</Badge>}
              </div>
              <div className="space-y-2 mb-4">
                {gPlayers.map(p => (
                  <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 bg-[#F4F6F5] rounded-lg" data-testid={`confirm-player-${p.id}`}>
                    <div className="w-8 h-8 rounded-full bg-[#E8E5DB] flex items-center justify-center text-[#1B4332] font-bold text-xs flex-shrink-0">
                      {p.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-[#1D242B]">{p.name}</span>
                    <Badge className="bg-[#1B4332] text-white text-[10px] font-bold px-1.5 ml-auto flex-shrink-0">
                      WHS {displayWHS(p)}
                    </Badge>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 h-12 rounded-xl border-[#E1E6E4] text-[#455561] font-semibold"
                  onClick={() => setConfirmGroupIdx(null)} data-testid="confirm-group-cancel">Cancel</Button>
                <Button className="flex-1 h-12 rounded-xl bg-[#1B4332] hover:bg-[#163B2B] text-white font-semibold"
                  onClick={() => { const idx = confirmGroupIdx; setConfirmGroupIdx(null); selectGroup(idx); }}
                  data-testid="confirm-group-submit">Confirm</Button>
              </div>
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>

    {/* Session expired */}
    <AlertDialog open={sessionExpired} onOpenChange={() => {}}>
      <AlertDialogContent className="sm:max-w-sm rounded-2xl" onPointerDownOutside={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-[#1D242B]">Session Expired</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#455561]">Your scoring session has expired. Please log back in.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => navigate(`/trip/${code}`)} className="w-full bg-[#1B4332] text-white rounded-xl font-bold">Log back in</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <div className="min-h-screen bg-[#F4F6F5] pb-safe max-w-[100vw] overflow-x-hidden" data-testid="scramble-scoring-screen">
      {/* ── Header bar (identical to individual) ── */}
      <div className="bg-[#1D242B] px-4 py-3">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate(`/trip/${code}/round/${roundId}/setup`)} className="text-white p-1 flex-shrink-0" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" weight="bold" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg text-white font-bold truncate">{round.label}</h1>
            <p className="text-xs text-[#B8C7C0] truncate">{round.course?.name}{currentTeeTime ? ` · ${currentTeeTime}` : ''}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => { sessionStorage.setItem('ft_scoring_hole', String(currentHole)); navigate(`/trip/${code}/round/${roundId}/leaderboard`); }}
            className="text-[#E59000] h-8 px-2 flex-shrink-0" data-testid="go-leaderboard-btn">
            <ChartBar className="w-4 h-4 mr-1" weight="fill" /> Board
          </Button>
          <SyncStatusPill unsavedCount={unsavedHoles.size} isSaving={savingKey !== null} lastSynced={lastSynced} pendingOps={[]} onRetryAll={() => {}} />
        </div>
        {/* ── Hole nav pills (identical) ── */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 ft-scroll">
          {Array.from({length: 18}, (_, i) => {
            const scored = (teamStrokes[i] || 0) > 0;
            return (
              <button key={i} onClick={() => setCurrentHole(i)}
                className={`w-9 h-9 rounded-full flex-shrink-0 text-sm font-bold transition-all ${
                  i === currentHole ? 'bg-[#1B4332] text-white scale-110' :
                  scored ? 'bg-[#E8E5DB] text-[#1B4332]' : 'bg-[#2C3742] text-[#B8C7C0]'
                }`} data-testid={`hole-btn-${i}`}>{i + 1}</button>
            );
          })}
        </div>
      </div>

      {/* ── Hole info bar (identical to individual) ── */}
      <div className="bg-white border-b border-[#E1E6E4] px-4 py-3 flex items-center justify-between">
        <div>
          <span className="text-2xl font-bold text-[#1D242B]">Hole {currentHole + 1}</span>
          <span className="text-base text-[#455561] ml-2">Par {par}</span>
        </div>
      </div>

      <InstallPrompt variant="scoring" />

      {/* ── Score lock banner (identical to individual) ── */}
      {(isRevealed || isGroupFinished) && (
        <div className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold ${
          scoreLocked
            ? 'bg-[#FFF3E0] text-[#E59000] border-b border-[#FFE0B2]'
            : 'bg-[#E8F5E9] text-[#2E7D32] border-b border-[#C8E6C9]'
        }`} data-testid="score-lock-banner">
          {scoreLocked ? (
            <>
              <Lock className="w-4 h-4 flex-shrink-0" weight="bold" />
              <span className="flex-1">{isGroupFinished && !isRevealed ? 'Group finished' : 'Round revealed'} — scores locked</span>
              <button onClick={() => setScoreUnlockDialogOpen(true)}
                className="ml-auto px-3 py-1 text-xs font-bold bg-[#E59000] text-white rounded-lg hover:bg-[#D48400] transition-colors whitespace-nowrap"
                data-testid="unlock-score-edit-btn">Unlock (owner)</button>
            </>
          ) : (
            <>
              <LockOpen className="w-4 h-4 flex-shrink-0" weight="bold" />
              <span>Unlocked — edits will change the {isRevealed ? 'revealed leaderboard' : 'final scores'}</span>
            </>
          )}
        </div>
      )}
      <OwnerPasswordDialog open={scoreUnlockDialogOpen} onOpenChange={setScoreUnlockDialogOpen}
        onSuccess={(token) => { setScoreUnlockDialogOpen(false); setScoreEditUnlockToken(code, roundId, token); setScoreEditUnlocked(true); }}
        description="This round has been revealed. Enter the master password to unlock score editing." tripCode={code} />

      {/* ── Group bar (matches individual's selected-group-bar) ── */}
      <div className="px-4 pt-3">
        <div className="flex items-center justify-between mb-2" data-testid="selected-group-bar">
          <div className="flex items-center gap-2">
            <UsersThree className="w-4 h-4 text-[#1B4332]" weight="fill" />
            <span className="text-sm font-semibold text-[#1D242B]">Scoring: {selectedGroupName}</span>
          </div>
          {isOrganiser && (
            <button onClick={() => setShowGroupPicker(true)}
              className="text-xs text-[#1B4332] font-semibold flex items-center gap-1" data-testid="change-group-btn">
              <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" /> Change
            </button>
          )}
        </div>
      </div>

      <div className="px-4 pb-4 max-w-lg mx-auto space-y-3">
        {/* ── Team score card (mirrors EditablePlayerCard layout) ── */}
        <Card className="bg-white border-[#E1E6E4] rounded-xl p-4" data-testid="team-score-card">
          {/* Header row — team name + players + badges (mirrors player card header) */}
          <div className="flex items-center justify-between mb-1 gap-2">
            <div className="min-w-0 flex-1">
              <span className="font-semibold text-[#1D242B] text-sm truncate block">{selectedGroupName}</span>
              <span className="text-[10px] text-[#455561] block break-words leading-relaxed">
                {teamPlayers.map(p => {
                  const ph = getEffectivePH(p, effectiveCourseForPlayer(round, p.id));
                  return `${p.name} (PH: ${formatPlayingHandicap(ph)})`;
                }).join(', ')}
              </span>
            </div>
            <Badge className="bg-[#1B4332] text-white text-[10px] font-bold px-1.5 flex-shrink-0">Team PH {formatPlayingHandicap(teamPH)}</Badge>
            {shots > 0 && <Badge className="bg-[#E59000] text-white text-[10px] font-bold px-1.5 flex-shrink-0">+{shots}</Badge>}
          </div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] text-[#455561]">SI {si}</span>
            <div className="flex items-center gap-1">
              {unsavedHoles.has(`team-${currentHole}`) && <CloudSlash className="w-4 h-4 text-[#D32F2F]" />}
              {fmt !== 'nsp' && teamStab >= 0 && !isHoleNr && (
                <span className={`text-sm font-bold ${teamStab >= 3 ? 'text-[#D32F2F]' : teamStab >= 2 ? 'text-[#1B4332]' : 'text-[#455561]'}`}>
                  {teamStab} pts
                </span>
              )}
              {fmt !== 'nsp' && (
                <span className="text-[10px] text-[#B8C7C0] ml-1">Total: {teamTotal}</span>
              )}
            </div>
          </div>
          {/* Score controls (identical to individual EditablePlayerCard) */}
          <div className="flex items-center justify-center gap-3">
            {isHoleNr ? (
              <div className="flex flex-col items-center gap-1.5">
                <button onClick={!scoreLocked ? handleToggleNr : undefined} disabled={scoreLocked}
                  className="w-20 h-20 rounded-2xl bg-[#FFF3E0] border-2 border-[#E59000]/30 flex items-center justify-center text-2xl font-bold text-[#E59000] active:bg-[#FFE0B2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="nr-display">NR</button>
                {!scoreLocked && <span className="text-[10px] font-semibold text-[#E59000]">Tap to undo</span>}
              </div>
            ) : (
              <>
                <button onClick={() => handleTeamStroke(-1)} disabled={scoreLocked}
                  className="w-14 h-14 rounded-2xl bg-[#F4F6F5] flex items-center justify-center active:bg-[#E9EDEB] disabled:opacity-30 transition-colors flex-shrink-0"
                  data-testid="team-minus">
                  <Minus className="w-6 h-6 text-[#1D242B]" weight="bold" />
                </button>
                {editing ? (
                  <input ref={inputRef} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={2}
                    value={inputVal} onChange={(e) => setInputVal(e.target.value.replace(/\D/g, ''))}
                    onBlur={commitEdit} onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                    className={`w-20 h-20 rounded-2xl text-center text-3xl font-bold flex-shrink-0 outline-none ring-2 ring-[#1B4332] ${teamGross > 0 ? colorClass : 'bg-[#F4F6F5] text-[#1D242B]'}`}
                    data-testid="team-score-input" />
                ) : (
                  <button onClick={startEdit} disabled={scoreLocked}
                    className={`w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold transition-all flex-shrink-0 ${
                      teamGross > 0 ? colorClass : 'bg-[#F4F6F5] text-[#B8C7C0] italic opacity-70'
                    } ${scoreLocked ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="team-score-display">
                    {teamGross > 0 ? teamGross : par}
                  </button>
                )}
                <button onClick={() => handleTeamStroke(1)} disabled={scoreLocked}
                  className="w-14 h-14 rounded-2xl bg-[#F4F6F5] flex items-center justify-center active:bg-[#E9EDEB] disabled:opacity-30 transition-colors flex-shrink-0"
                  data-testid="team-plus">
                  <Plus className="w-6 h-6 text-[#1D242B]" weight="bold" />
                </button>
                {fmt !== 'nsp' && !scoreLocked && (
                  <button onClick={() => { if (editing) { setEditing(false); setInputVal(''); } handleToggleNr(); }}
                    className="w-11 h-11 rounded-xl bg-[#FFF3E0] border border-[#E59000]/30 text-[#E59000] text-xs font-bold flex items-center justify-center flex-shrink-0 active:bg-[#FFE0B2] transition-colors"
                    data-testid="team-mark-nr">NR</button>
                )}
              </>
            )}
          </div>
        </Card>

        {/* ── Drive owner picker ── */}
        <Card className="bg-white border-[#E1E6E4] rounded-xl p-4" data-testid="drive-owner-card">
          <p className="text-xs font-semibold text-[#455561] mb-2">Whose drive?</p>
          <div className="grid grid-cols-2 gap-2">
            {teamPlayers.map(p => {
              const isSelected = currentDriveOwner === p.id;
              const dc = driveCounts[p.id] || 0;
              const lockedOut = driverLock.lockActive && !driverLock.mustTake.includes(p.id);
              const isDisabled = scoreLocked || lockedOut;
              return (
                <button key={p.id} onClick={() => !isDisabled && handleSetDriveOwner(p.id)} disabled={isDisabled}
                  className={`min-h-[44px] p-2.5 rounded-xl border-2 text-center transition-all ${
                    isSelected ? 'border-[#1B4332] bg-[#1B4332] shadow-md'
                    : lockedOut ? 'border-[#E1E6E4] bg-[#F4F6F5] opacity-40 cursor-not-allowed'
                    : 'border-[#E1E6E4] bg-white hover:border-[#1B4332]/50'
                  } ${scoreLocked ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid={`drive-owner-${p.id}`}>
                  <p className={`text-xs font-bold break-words ${isSelected ? 'text-white' : lockedOut ? 'text-[#B8C7C0]' : 'text-[#1D242B]'}`}>{p.name}</p>
                  <p className={`text-[10px] tabular-nums ${isSelected ? 'text-white/70' : lockedOut ? 'text-[#B8C7C0]' : 'text-[#455561]'}`}>{dc}/{minDrives}</p>
                </button>
              );
            })}
          </div>
          {driverLock.lockMessage && (
            <div className={`mt-2 px-3 py-2 rounded-xl text-xs font-semibold ${
              driverLock.lockMessage.type === 'hard'
                ? 'bg-[#FFEBEE] text-[#D32F2F] border border-[#FFCDD2]'
                : 'bg-[#FFF3E0] text-[#E59000] border border-[#FFE0B2]'
            }`} data-testid="drive-lock-message">
              {driverLock.lockMessage.text}
            </div>
          )}
        </Card>

        {/* ── Drive warnings ── */}
        {warns.length > 0 && (
          <div className="space-y-1" data-testid="drive-warnings">
            {warns.map(w => {
              const p = players.find(pl => pl.id === w.playerId);
              return (
                <div key={w.playerId} className={`px-3 py-2 rounded-xl text-xs font-semibold ${
                  w.severity === 'hard' ? 'bg-[#FFEBEE] text-[#D32F2F] border border-[#FFCDD2]' : 'bg-[#FFF3E0] text-[#E59000] border border-[#FFE0B2]'
                }`} data-testid={`drive-warn-${w.playerId}`}>
                  {w.severity === 'hard'
                    ? `${p?.name || '?'} can no longer meet the minimum (needs ${w.drivesNeeded} more, only ${w.holesRemaining} hole${w.holesRemaining !== 1 ? 's' : ''} left)`
                    : `${p?.name || '?'} needs ${w.drivesNeeded} more drive${w.drivesNeeded !== 1 ? 's' : ''}, ${w.holesRemaining} hole${w.holesRemaining !== 1 ? 's' : ''} left`
                  }
                </div>
              );
            })}
          </div>
        )}

        {/* ── Nav buttons (identical to individual) ── */}
        <div className="flex items-center justify-between pt-2">
          <Button onClick={handlePrev} disabled={currentPosInOrder === 0} variant="outline"
            className="rounded-xl h-12 px-4 border-[#E1E6E4]" data-testid="prev-hole-btn">
            <CaretLeft className="w-5 h-5 mr-1" /> Prev
          </Button>
          <Button onClick={handleNext}
            className={`rounded-xl h-12 px-6 ${
              currentPosInOrder === 17 ? 'bg-[#E59000] hover:bg-[#CC8000]' : 'bg-[#1B4332] hover:bg-[#163B2B]'
            } text-white font-semibold`} data-testid="next-hole-btn">
            {currentPosInOrder === 17 ? (isGroupFinished ? 'Back to Round' : 'Finish Round') : `Next — Hole ${playingOrder[currentPosInOrder + 1] + 1}`}
          </Button>
        </div>
      </div>
    </div>


    {/* ── All Par confirmation (matches individual's dialog) ── */}
    <AlertDialog open={allParConfirmOpen} onOpenChange={setAllParConfirmOpen}>
      <AlertDialogContent className="max-w-sm rounded-xl" data-testid="all-par-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1D242B]">No Scores Entered</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#455561]">
            No score was entered for <span className="font-semibold text-[#1D242B]">Hole {currentHole + 1}</span>. Confirm the team scored Par?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="all-par-cancel-btn">Go back</AlertDialogCancel>
          <AlertDialogAction
            className="bg-[#1B4332] hover:bg-[#163B2B] text-white"
            data-testid="all-par-confirm-btn"
            onClick={handleAllParConfirm}>
            Confirm team score
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* ── Missing holes dialog ── */}
    <AlertDialog open={!!missingHolesDialog} onOpenChange={() => setMissingHolesDialog(null)}>
      <AlertDialogContent className="max-w-sm rounded-xl" data-testid="scramble-missing-holes-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1D242B]">Missing Holes</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#455561]">
            Holes {missingHolesDialog?.map(h => h + 1).join(', ')} have no score. Mark them as NR and finish?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => { setMissingHolesDialog(null); setCurrentHole(missingHolesDialog?.[0] ?? 0); }}>Go to first missing</AlertDialogCancel>
          <AlertDialogAction onClick={handleMissingMarkNr} className="bg-[#1B4332] hover:bg-[#163B2B] text-white">Mark NR &amp; Finish</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* ── Drive under-minimum dialog ── */}
    <AlertDialog open={!!driveUnderMinDialog} onOpenChange={() => setDriveUnderMinDialog(null)}>
      <AlertDialogContent className="max-w-sm rounded-xl" data-testid="scramble-under-min-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1D242B]">Minimum Drives Not Met</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-[#455561] space-y-2">
              <p>The following players finished under the minimum drive count ({minDrives}):</p>
              <ul className="space-y-1">
                {driveUnderMinDialog?.map(w => {
                  const p = players.find(pl => pl.id === w.playerId);
                  return <li key={w.playerId} className="font-semibold text-[#1D242B]">{p?.name || '?'} ({w.drivesUsed}/{minDrives})</li>;
                })}
              </ul>
              <p className="text-xs text-[#B8C7C0]">Continue anyway?</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Go back</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setDriveUnderMinDialog(null); setFinishConfirmOpen(true); }} className="bg-[#E59000] text-white">Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* ── Finish confirmation (identical to individual) ── */}
    <AlertDialog open={finishConfirmOpen} onOpenChange={setFinishConfirmOpen}>
      <AlertDialogContent className="max-w-sm rounded-xl" data-testid="finish-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1D242B]">Finish Round</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#455561]">
            Please confirm you have finished your round — no changes can be made after.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="finish-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-[#1B4332] hover:bg-[#163B2B] text-white" data-testid="finish-confirm-btn"
            onClick={() => { setFinishConfirmOpen(false); doFinishRound(); }}>Confirm and Finish Round</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <InstallPrompt />
    </>
  );
}
